import React, { useState, useEffect, useRef } from 'react';
import { collection, query, onSnapshot, addDoc, serverTimestamp, orderBy } from 'firebase/firestore';

const CustomOrderChatModal = ({ order, onClose, db, user, setModalMessage }) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const messagesEndRef = useRef(null);

    const isUser = user?.uid === order.userId;
    const isOwner = order.userId;

    // Fetch messages for the specific order
    useEffect(() => {
        if (!db || !isOwner || !order.id) {
            setModalMessage("Error: Could not retrieve messages.");
            return;
        }

        const messagesCollectionRef = collection(db, `users/${isOwner}/customOrders/${order.id}/messages`);
        const q = query(messagesCollectionRef, orderBy('timestamp'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedMessages = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setMessages(fetchedMessages);
            setLoading(false);
        }, (err) => {
            console.error("Error fetching chat messages:", err);
            setModalMessage("Error loading messages. Please try again.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [db, isOwner, order.id, setModalMessage]);

    // Scroll to the bottom of the chat on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (newMessage.trim() === '') return;

        if (!db || !isOwner || !order.id) {
            setModalMessage("Error: Database connection lost. Cannot send message.");
            return;
        }

        try {
            const messagesCollectionRef = collection(db, `users/${isOwner}/customOrders/${order.id}/messages`);
            await addDoc(messagesCollectionRef, {
                text: newMessage,
                senderId: user.uid,
                senderName: isUser ? "You" : "Admin", // Simplified for demo
                timestamp: serverTimestamp()
            });
            setNewMessage('');
        } catch (error) {
            console.error("Error sending message:", error);
            setModalMessage(`Failed to send message: ${error.message}`);
        }
    };

    const getSenderColor = (senderId) => {
        // Use different colors for user and admin
        return senderId === isOwner ? 'bg-[#316dae]' : 'bg-green-600';
    };

    // Function to get status styling
    const getStatusStyle = (status) => {
        switch (status) {
            case 'pending': return 'bg-yellow-600 shadow-yellow-500/25';
            case 'in progress': return 'bg-blue-600 shadow-blue-500/25';
            case 'completed': return 'bg-green-600 shadow-green-500/25';
            case 'cancelled': return 'bg-red-600 shadow-red-500/25';
            default: return 'bg-gray-600 shadow-gray-500/25';
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="glass-card rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col border border-white/20 animate-bounceIn overflow-hidden" style={{ maxHeight: '90vh' }}>
                {/* Enhanced Header */}
                <div className="relative bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-b border-white/10 p-6">
                    {/* Background pattern */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 animate-pulse"></div>
                    
                    <div className="relative flex justify-between items-center">
                        <div className="flex items-center space-x-4">
                            {/* Order icon */}
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600/30 to-purple-600/30 flex items-center justify-center animate-float">
                                <i className="fas fa-shopping-basket text-blue-400 text-2xl"></i>
                            </div>
                            
                            <div className="space-y-1">
                                <div className="flex items-center space-x-3">
                                    <h3 className="text-2xl font-bold gradient-text animate-slideInLeft">{order.title}</h3>
                                    <span className={`px-3 py-1 rounded-xl text-xs font-bold ${getStatusStyle(order.status)} text-white animate-pulse`}>
                                        {order.status.toUpperCase()}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-4 text-sm text-gray-400 animate-slideInLeft" style={{ animationDelay: '0.2s' }}>
                                    <div className="flex items-center space-x-2">
                                        <i className="fas fa-calendar"></i>
                                        <span>Order #{order.id.slice(-6)}</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <i className="fas fa-dollar-sign text-green-400"></i>
                                        <span className="text-green-400 font-semibold">{order.budget}</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
                                        <span>{messages.length} messages</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <button 
                            onClick={onClose} 
                            className="group relative w-12 h-12 rounded-xl bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/50 transition-all duration-300 hover:scale-110 hover:rotate-90"
                        >
                            <i className="fas fa-times text-gray-400 group-hover:text-red-400 text-xl transition-colors duration-300"></i>
                            <div className="absolute inset-0 rounded-xl bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </button>
                    </div>
                </div>
                
                {/* Enhanced Messages Area */}
                <div className="flex-grow overflow-y-auto custom-scrollbar p-6 space-y-6">
                    {loading ? (
                        <div className="flex justify-center items-center h-full animate-fadeIn">
                            <div className="text-center">
                                <div className="relative mb-6">
                                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500/20 border-t-blue-500 mx-auto"></div>
                                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 animate-pulse"></div>
                                </div>
                                <p className="text-xl font-semibold gradient-text animate-shimmer">Loading Messages</p>
                                <p className="text-gray-400 text-sm mt-2">Fetching conversation history...</p>
                            </div>
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="text-center py-16 animate-fadeIn">
                            <div className="relative mb-8">
                                <div className="w-24 h-24 mx-auto rounded-3xl bg-gradient-to-br from-blue-600/20 to-purple-600/20 flex items-center justify-center animate-float">
                                    <i className="fas fa-comment-dots text-5xl text-blue-400 animate-pulse"></i>
                                </div>
                                <div className="absolute top-2 right-8 w-3 h-3 rounded-full bg-blue-400/60 animate-bounce" style={{ animationDelay: '0.5s' }}></div>
                                <div className="absolute bottom-4 left-6 w-2 h-2 rounded-full bg-purple-400/60 animate-bounce" style={{ animationDelay: '1s' }}></div>
                            </div>
                            <h4 className="text-2xl font-bold gradient-text mb-3 animate-slideInLeft">Start the Conversation!</h4>
                            <p className="text-gray-300 max-w-md mx-auto leading-relaxed animate-slideInLeft" style={{ animationDelay: '0.2s' }}>
                                This is your direct line to our development team. Share updates, ask questions, or discuss your project details.
                            </p>
                            <div className="mt-8 grid grid-cols-3 gap-4 max-w-sm mx-auto animate-slideInLeft" style={{ animationDelay: '0.4s' }}>
                                <div className="text-center">
                                    <div className="w-12 h-12 mx-auto rounded-xl bg-blue-500/10 flex items-center justify-center mb-2">
                                        <i className="fas fa-bolt text-blue-400"></i>
                                    </div>
                                    <p className="text-xs text-gray-400">Real-time</p>
                                </div>
                                <div className="text-center">
                                    <div className="w-12 h-12 mx-auto rounded-xl bg-green-500/10 flex items-center justify-center mb-2">
                                        <i className="fas fa-shield-alt text-green-400"></i>
                                    </div>
                                    <p className="text-xs text-gray-400">Secure</p>
                                </div>
                                <div className="text-center">
                                    <div className="w-12 h-12 mx-auto rounded-xl bg-purple-500/10 flex items-center justify-center mb-2">
                                        <i className="fas fa-history text-purple-400"></i>
                                    </div>
                                    <p className="text-xs text-gray-400">Tracked</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {messages.map((msg, index) => (
                                <div 
                                    key={msg.id} 
                                    className={`flex ${msg.senderId === user.uid ? 'justify-end' : 'justify-start'} animate-slideInLeft`}
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                >
                                    <div className={`group max-w-xs md:max-w-md relative transition-all duration-300 hover:scale-105 hover:-translate-y-1`}>
                                        {/* Message bubble */}
                                        <div className={`relative p-6 rounded-2xl shadow-lg transition-all duration-300 ${
                                            msg.senderId === user.uid 
                                                ? 'bg-gradient-to-br from-blue-600 to-purple-600 text-white ml-4' 
                                                : 'bg-gradient-to-br from-gray-700 to-gray-800 text-white mr-4'
                                        } hover:shadow-2xl`}>
                                            {/* Shimmer overlay */}
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-2xl"></div>
                                            
                                            {/* Sender badge */}
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center space-x-2">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                                        msg.senderId === user.uid ? 'bg-white/20' : 'bg-blue-500/20'
                                                    }`}>
                                                        <i className={`fas ${msg.senderId === user.uid ? 'fa-user' : 'fa-headset'} text-sm`}></i>
                                                    </div>
                                                    <span className="font-bold text-sm">{msg.senderName}</span>
                                                </div>
                                                {msg.senderId !== user.uid && (
                                                    <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full font-semibold">
                                                        Developer
                                                    </span>
                                                )}
                                            </div>
                                            
                                            {/* Message content */}
                                            <p className="text-sm leading-relaxed relative z-10">{msg.text}</p>
                                            
                                            {/* Timestamp with enhanced styling */}
                                            <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
                                                <div className="text-xs opacity-75 flex items-center space-x-1">
                                                    <i className="fas fa-clock"></i>
                                                    <span>
                                                        {msg.timestamp?.toDate ? msg.timestamp.toDate().toLocaleTimeString([], {
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        }) : '...'}
                                                    </span>
                                                </div>
                                                {msg.senderId === user.uid && (
                                                    <div className="flex items-center space-x-1">
                                                        <i className="fas fa-check-double text-xs text-green-400"></i>
                                                        <span className="text-xs text-green-400">Sent</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Message tail */}
                                        <div className={`absolute top-6 ${
                                            msg.senderId === user.uid 
                                                ? '-right-2 border-l-8 border-l-blue-600 border-t-8 border-b-8 border-t-transparent border-b-transparent' 
                                                : '-left-2 border-r-8 border-r-gray-700 border-t-8 border-b-8 border-t-transparent border-b-transparent'
                                        }`}></div>
                                    </div>
                                </div>
                            ))}
                            <div ref={messagesEndRef} />
                        </div>
                    )}
                </div>
                
                {/* Enhanced Message Input Area */}
                <div className="relative bg-gradient-to-r from-gray-800/50 to-gray-900/50 border-t border-white/10 p-6">
                    {/* Background pattern */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5"></div>
                    
                    <form onSubmit={handleSendMessage} className="relative">
                        <div className="flex items-end space-x-4">
                            {/* Message input */}
                            <div className="flex-grow relative">
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        placeholder="Type your message here... 💬"
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 px-6 pr-16 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/10"
                                        disabled={loading}
                                    />
                                    
                                    {/* Character count */}
                                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                                        <span className={`text-xs font-medium ${
                                            newMessage.length > 500 ? 'text-red-400' : 
                                            newMessage.length > 400 ? 'text-yellow-400' : 
                                            'text-gray-500'
                                        }`}>
                                            {newMessage.length}/500
                                        </span>
                                    </div>
                                </div>
                                
                                {/* Message tips */}
                                <div className="mt-2 flex items-center justify-between">
                                    <div className="flex items-center space-x-4 text-xs text-gray-400">
                                        <div className="flex items-center space-x-1">
                                            <i className="fas fa-lightbulb text-yellow-400"></i>
                                            <span>Tip: Be specific about your requirements</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                                        <i className="fas fa-shield-alt"></i>
                                        <span>End-to-end encrypted</span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Send button */}
                            <button
                                type="submit"
                                disabled={!newMessage.trim() || loading}
                                className="group relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 disabled:from-gray-600 disabled:to-gray-700 text-white font-bold py-4 px-6 rounded-2xl transition-all duration-500 shadow-lg hover:shadow-2xl hover:shadow-blue-500/30 hover:-translate-y-1 hover:scale-105 disabled:hover:translate-y-0 disabled:hover:scale-100 disabled:opacity-50"
                            >
                                {/* Shimmer overlay */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                
                                {/* Loading spinner */}
                                {loading && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/20 border-t-white"></div>
                                    </div>
                                )}
                                
                                <div className={`relative flex items-center space-x-2 ${loading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}>
                                    <span className="font-semibold">Send</span>
                                    <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
                                        <i className="fas fa-paper-plane text-sm"></i>
                                    </div>
                                </div>
                                
                                {/* Glow effect */}
                                <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></div>
                            </button>
                        </div>
                        
                        {/* Quick actions */}
                        <div className="mt-4 flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setNewMessage("Could you please provide an update on the progress?")}
                                    className="text-xs text-gray-400 hover:text-blue-400 transition-colors duration-200 flex items-center space-x-1"
                                >
                                    <i className="fas fa-question-circle"></i>
                                    <span>Request Update</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setNewMessage("Thank you for the update! This looks great.")}
                                    className="text-xs text-gray-400 hover:text-green-400 transition-colors duration-200 flex items-center space-x-1"
                                >
                                    <i className="fas fa-heart"></i>
                                    <span>Quick Thanks</span>
                                </button>
                            </div>
                            
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                                <span>Connected</span>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CustomOrderChatModal;
